import { Theme, useMediaQuery } from "@material-ui/core";
import { DeleteForever } from "@material-ui/icons";
import { ConfirmButton } from "../../Components/ConfirmButton";
import * as React from "react";
import { FC, Fragment } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  FunctionField,
  ListContextProvider,
  ListProps,
  SelectInput,
  TextField,
  TextInput,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";
import { useAppDataProvider } from "../../dataprovider";
import { useAuth } from "@airborne/react-admin";
import { EnumField } from "../../../src/Components/EnumField";
import CreatedByField from "../../../src/Components/ReferenceFields/CreatedBy/CreatedByField";
import { RequestExcusableShippingType } from "../../../src/Enums/RequestExcusableShippingType";
import { RequestExcusableStatus } from "../../../src/Enums/RequestExcusableStatus";
import { ExcelExport } from "../../../src/Helper/ExportExcel";
import { ExcusableExcelHeader, ExcusableExcelName } from "../../../src/Pages/Excusable/Constant/Excel";
import { CustomDateField } from "../../Components/CustomDateField";
import { CustomDatetime } from "../../Components/CustomDatetime";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../Constants/Constant";
import { excusableStatus, shippingTypes } from "./Constant/Source";
import ExcusableDetailButton from "./ExcusableDetailButton";
import PoolList from "../../Components/PoolListComponent/PoolListComponent";
import CopyableTableWrapper from "../../../src/Components/CopyableTableWrapper";

const ExcusablePBHFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <TextInput label="Ref No" source="refNo__contains" alwaysOn />
    <TextInput label="Customer Code" source="customerCode__contains" alwaysOn />
    <TextInput label="Pn" source="pn__contains" alwaysOn />
    <TextInput label="Sn" source="sn__contains" alwaysOn />
    <SelectInput
      source="shippingType__eq"
      alwaysOn
      label="SHIPPING TYPE"
      choices={shippingTypes}
      optionText="label"
      optionValue="id"
    />
    <SelectInput
      source="excusableStatus__eq"
      alwaysOn
      label="Status"
      choices={excusableStatus}
      optionText="label"
      optionValue="id"
    />
    <CustomDatetime source="createdDate__gte" label="Created Date (>=)" alwaysOn />
    <CustomDatetime source="createdDate__lte" label="Created Date (<=)" alwaysOn />
    <CustomDatetime source="requestedDeliveryDate__gte" label="Requested Delivery Date(>=)" alwaysOn />
    <CustomDatetime source="requestedDeliveryDate__lte" label="Requested Delivery Date(<=)" alwaysOn />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    ExcusableExcelHeader,
    data.map(v => [
      v.refNo,
      v.customerCode, // Burası düzenlenecek.!
      v.requestCreateDate,
      v.pn,
      v.sn,
      v.flightDate,
      v.estimatedDeliveryDate,
      RequestExcusableShippingType[v.shippingType],
      v.createdBy,
      v.createdDate,
      v.requestedDeliveryDate,
      RequestExcusableStatus[v.excusableStatus],
    ]),
    ExcusableExcelName
  );
};

const PBHDatagrid: FC = props => {
  const listContext = useListContext();
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useAppDataProvider();
  const { user } = useAuth();

  const handleDelete = (record: any) => {
    dataProvider
      .deleteRequestExcusable(record?.id)
      .then(() => {
        notify('Record deleted', 'success', false, true);
        refresh();
      })
      .catch(error => {
        notify(`Error: ${error?.response?.data?.message ?? error}`, 'error', false, true);
      });
  };

  return (
    <Fragment>
      {isXSmall ? (
        <ListContextProvider value={{ ...listContext }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          value={{
            ...listContext,
          }}
        >
          <CopyableTableWrapper>
            <Datagrid {...props} hasBulkActions={false} optimized>
              <FunctionField
                label="Actions"
                render={(record: any) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ExcusableDetailButton resource="requestorderexcusables" record={record} />
                    {record?.excusableStatus !== RequestExcusableStatus.Rejected && ((user?.isCustomer && record.shippingType === RequestExcusableShippingType.Unserviceable) || (!user?.isCustomer  && record.shippingType === RequestExcusableShippingType.Serviceable)) ?(
                        <ConfirmButton
                          style={{ color: '#C90C0F' }}
                          title="Delete Record"
                          content="Are you sure you want to delete this record?"
                          click={() => handleDelete(record)}
                        >
                          <DeleteForever fontSize="small" />
                        </ConfirmButton>
                      ) : null}
                  </div>
                )}
              />
              <TextField source="refNo" label={"Ref No"} />
              <TextField source="customerCode" label={"Customer Name"} />
              <CustomDateField source="requestCreateDate" label={"Request Date"} showTime={false} locales="en-GB" />
              <TextField source="pn" label={"PN"} />
              <TextField source="sn" label={"SN"} />
              <EnumField source={"shippingType"} label={"Shipping Type"} enumName={"RequestExcusableShippingType"} />
              <CreatedByField label="Created by" link={false} />
              <CustomDateField source="createdDate" locales="en-GB" />
              <CustomDateField source="requestedDeliveryDate" locales="en-GB" />
              <EnumField source={"excusableStatus"} label={"Status"} enumName={"RequestExcusableStatus"} />
            </Datagrid>
          </CopyableTableWrapper>
        </ListContextProvider>
      )}
    </Fragment>
  );
};

const ExcusableListPBH: FC<ListProps> = props => (
  <PoolList
    {...props}
    title="PBH Excusable List"
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    basePath="/requestorderexcusables"
    resource="requestorderexcusables"
    exporter={ExportToExcel}
    hasCreate={true}
    filters={<ExcusablePBHFilter />}
  >
    <PBHDatagrid />
  </PoolList>
);

export default ExcusableListPBH;
